import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { FEATURE_FLAGS } from '@/constants';

// ================================
// Module State
// ================================
/**
 * In-memory singleton reference for the browser telemetry client.
 *
 * Why a module-level singleton:
 * - Prevents creating multiple Application Insights clients during client-side navigation.
 * - Avoids duplicate telemetry initialization and event handlers.
 * - Keeps setup cost low after the first successful initialization.
 *
 * This value is intentionally nullable:
 * - null before initialization.
 * - a live ApplicationInsights instance after initialization.
 */
let instance: ApplicationInsights | null = null;

/**
 * Parses an Application Insights connection string into key/value pairs.
 *
 * Expected format example:
 * InstrumentationKey=xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx;IngestionEndpoint=https://...
 */
const parseConnectionString = (value: string): Record<string, string> => {
  return value
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, part) => {
      const separatorIndex = part.indexOf('=');
      if (separatorIndex <= 0) return acc;

      const key = part.slice(0, separatorIndex).trim().toLowerCase();
      const parsedValue = part.slice(separatorIndex + 1).trim();
      if (!key || !parsedValue) return acc;

      acc[key] = parsedValue;
      return acc;
    }, {});
};

/**
 * Minimal preflight check for an Application Insights connection string.
 *
 * We require a non-empty InstrumentationKey because the SDK throws if it is missing.
 * This guard prevents the error from bubbling into the Next.js runtime overlay.
 */
const isValidConnectionString = (value: string): boolean => {
  const parsed = parseConnectionString(value);
  const instrumentationKey = parsed.instrumentationkey;

  if (!instrumentationKey) return false;

  // Accept canonical GUID format for instrumentation keys.
  const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  return guidPattern.test(instrumentationKey);
};

/**
 * Get (or lazily initialize) the browser Application Insights client.
 *
 * High-level flow:
 * 1) Confirm telemetry is enabled by feature flag.
 * 2) Confirm we are running in a browser runtime.
 * 3) Confirm a public connection string is available.
 * 4) Reuse existing singleton instance when present.
 * 5) Create, configure, and load a new instance once.
 *
 * Return contract:
 * - ApplicationInsights instance when telemetry can run.
 * - null when telemetry should not run in current conditions.
 *
 * Safety notes:
 * - This helper is safe to call from client components repeatedly.
 * - Guard clauses make behavior deterministic across environments.
 */
export function getApplicationInsights(): ApplicationInsights | null {
  // Feature-level kill switch.
  // If disabled, we do not initialize telemetry regardless of environment variables.
  if (!FEATURE_FLAGS.ENABLE_APP_INSIGHTS) return null;

  // Browser runtime guard.
  // Application Insights web SDK depends on browser APIs and should never run on the server.
  if (typeof window === 'undefined') return null;

  // Configuration guard.
  // The connection string must be exposed with NEXT_PUBLIC_ prefix for client-side use.
  // Missing value means telemetry is intentionally not configured for this deployment.
  const connectionString = process.env.NEXT_PUBLIC_APPLICATIONINSIGHTS_CONNECTION_STRING;
  if (!connectionString) return null;

  // Preflight validation guard.
  // Avoid calling SDK initialize when the connection string is malformed.
  if (!isValidConnectionString(connectionString)) {
    console.warn(
      'AppInsights is enabled, but NEXT_PUBLIC_APPLICATIONINSIGHTS_CONNECTION_STRING is invalid. Telemetry initialization skipped.',
    );
    return null;
  }

  // Fast path: return existing singleton when already initialized.
  // Prevents duplicate SDK bootstraps and duplicate event collection.
  if (instance) return instance;

  // Create SDK instance with explicit config.
  // Config choices here are tailored for Next.js route tracking done in AppInsights component.
  const nextInstance = new ApplicationInsights({
    config: {
      // Primary ingestion endpoint and instrumentation metadata.
      connectionString,

      // Keep fetch and XHR tracking enabled so API calls are captured.
      disableFetchTracking: false,
      disableAjaxTracking: false,

      // Prevent the fetch/XHR instrumentation from wrapping Next.js App Router
      // internal requests. RSC navigation/prefetch fetches stream their response
      // body, and the SDK's response handling corrupts that stream — which breaks
      // client-side navigation (surfacing as "n is not a function"). Excluding the
      // RSC (`?_rsc=`) and static asset (`/_next/`) requests leaves those streams
      // untouched while still capturing telemetry for real API calls.
      excludeRequestFromAutoTrackingPatterns: [/[?&]_rsc=/i, /\/_next\//i],

      // Route changes are tracked manually in the AppInsights component.
      // Disabling auto route tracking avoids duplicate page-view events.
      enableAutoRouteTracking: false,
    },
  });

  try {
    // Final SDK bootstrap step; registers core telemetry pipeline.
    nextInstance.loadAppInsights();
    instance = nextInstance;
  } catch (error) {
    console.warn(
      'Failed to initialize Application Insights. Telemetry initialization skipped.',
      error,
    );
    return null;
  }

  // Return initialized singleton for immediate use by callers.
  return instance;
}
